import { useEffect, useState } from 'react';

export interface VisualViewportBox {
  height: number;
  top: number;
}

/**
 * A shrunk visual viewport is, in practice, the on-screen keyboard.
 *
 * Anything smaller is the URL bar collapsing, a scrollbar, or pinch-zoom drift — none of which
 * should move a modal.
 */
const KEYBOARD_THRESHOLD_PX = 120;

/**
 * The slice of the screen the browser is actually showing, but only while it differs materially
 * from the layout viewport.
 *
 * `100dvh` does NOT shrink for the on-screen keyboard, so a modal centred in it is centred behind
 * the keyboard. iOS then scrolls the focused field into view and drags the whole fixed layer up
 * with it, taking the modal's header off the top of the screen — which is how the promo/gift-card
 * popup ends up looking "positioned wrong" once the code field is tapped
 * (Asana 1218577055036636, item 3).
 *
 * Returns `null` whenever the two viewports agree, which is every desktop browser and every mobile
 * screen with the keyboard closed. Callers then fall back to their normal layout, so this can only
 * change what happens while a keyboard is actually open.
 */
export default function useVisualViewportBox(enabled: boolean): VisualViewportBox | null {
  const [box, setBox] = useState<VisualViewportBox | null>(null);

  useEffect(() => {
    const viewport = typeof window === 'undefined' ? null : window.visualViewport;

    if (!enabled || !viewport) {
      setBox(null);

      return;
    }

    const sync = () => {
      const hidden = window.innerHeight - viewport.height;

      // `offsetTop` is how far the browser has already scrolled the visual viewport down the
      // layout viewport. Carrying it over is what keeps the modal pinned to what the customer can
      // see rather than to where the page thinks the top is.
      setBox(hidden > KEYBOARD_THRESHOLD_PX ? { height: viewport.height, top: viewport.offsetTop } : null);
    };

    sync();
    viewport.addEventListener('resize', sync);
    viewport.addEventListener('scroll', sync);

    return () => {
      viewport.removeEventListener('resize', sync);
      viewport.removeEventListener('scroll', sync);
    };
  }, [enabled]);

  return box;
}
